import * as React from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import {
  CaretUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";
import { Checkbox, type CheckedState } from "./checkbox";
import { useIsInsideDialog } from "./dialog";
import {
  CLEAR,
  CREATE,
  LOAD_MORE,
  canCreate,
  checkState,
  decideValueChange,
  defaultFilter,
  navigableValues,
  nearListEnd,
  resolveFilter,
  resolveItems,
  selectionValues,
  statusKind,
  toSections,
  toggleAllValues,
  visibleSections,
  type Section,
} from "./combobox-logic";

/**
 * A searchable picker on Base UI's Combobox: a trigger button that opens a
 * popup holding the search input and the list, single or `multiple` (chips in
 * the trigger), client- or server-filtered.
 *
 * Styled after the shadcn Base UI combobox, laid out like the cmdk
 * `Autocomplete` it replaces (search inside the popup, not in the trigger) so
 * the frontend's pickers keep their shape. cmdk stays for command palettes.
 *
 * Two layers:
 *
 *  - **`Combobox<T>`** — the batteries-included picker. Items are any `T`,
 *    identified by `getValue` and named by `getLabel`. Every user-visible
 *    string comes in through `labels` with an English default, because this
 *    package carries no i18n: the frontend passes translated strings.
 *  - **The parts** (`ComboboxRoot`, `ComboboxTrigger`, `ComboboxContent`, …),
 *    styled Base UI parts for anything the picker does not cover.
 *
 * Things that are not obvious from Base UI's docs:
 *
 *  1. **Action rows are items.** Load more, Clear and Create sit in the list
 *     as `Combobox.Item`s so the arrow keys reach them, as cmdk's did. Picking
 *     one is intercepted in `onValueChange` and cancelled through
 *     `eventDetails.cancel()`, which also stops Base UI closing the popup.
 *  2. **The query is owned here, not by Base UI.** Base UI clears the input
 *     after a multiple-mode pick and on close; the pickers this replaces kept
 *     the query, so only typing (`input-change`) is accepted.
 *  3. **Selections outlive the items that named them.** Every item seen is
 *     cached by value, so a server search that replaces `items` neither blanks
 *     the trigger nor drops a pick from the list (`pinSelected`).
 *  4. **`modal` defaults on inside a `DialogContent`** — see
 *     `useIsInsideDialog` — so the list stays wheel-scrollable under the
 *     dialog's scroll lock.
 *  5. **The popup layer is `z-index: 1500`** with `pointer-events: auto` on
 *     the positioner, the same as Popover and Select, so a picker opened in a
 *     dialog sits above it and stays clickable.
 */

/* -------------------------------------------------------------------------- */
/*                                    Parts                                   */
/* -------------------------------------------------------------------------- */

/**
 * Base UI 1.7.0 implements `autoHighlight="always"` — highlight the first item
 * as soon as the list opens — for every selection mode, but only types it on
 * `Autocomplete`. The picker needs it: Enter on a fresh list picks the top hit,
 * as cmdk did. The cast widens that one prop and nothing else.
 */
type ComboboxRootProps<
  Value,
  Multiple extends boolean | undefined = false,
> = Omit<ComboboxPrimitive.Root.Props<Value, Multiple>, "autoHighlight"> & {
  autoHighlight?: boolean | "always";
};

const ComboboxRoot = ComboboxPrimitive.Root as <
  Value,
  Multiple extends boolean | undefined = false,
>(
  props: ComboboxRootProps<Value, Multiple>,
) => React.JSX.Element;

/** Module scope so it is not a fresh object on every render. See Popover. */
const POSITIONER_STYLE: React.CSSProperties = {
  zIndex: 1500,
  pointerEvents: "auto",
};

type ComboboxTriggerProps = Omit<
  ComboboxPrimitive.Trigger.Props,
  "className"
> & {
  className?: string;
};

function ComboboxTrigger({
  className,
  children,
  ...props
}: ComboboxTriggerProps) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn(
        buttonVariants({ variant: "outline" }),
        "w-full min-w-0 justify-between bg-transparent",
        className,
      )}
      {...props}
    >
      {children}
      <CaretUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </ComboboxPrimitive.Trigger>
  );
}

type ComboboxContentProps = Omit<ComboboxPrimitive.Popup.Props, "className"> &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    "side" | "align" | "sideOffset" | "alignOffset" | "collisionPadding"
  > & {
    className?: string;
    /** Portal target; the document body by default. */
    container?: ComboboxPrimitive.Portal.Props["container"];
  };

function ComboboxContent({
  className,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  alignOffset,
  // Radix's 0, not Base UI's 5 — the same pin as Popover, so a picker at a
  // viewport edge sits where the Popover-based one did.
  collisionPadding = 0,
  container,
  ...props
}: ComboboxContentProps) {
  return (
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        data-slot="combobox-positioner"
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        style={POSITIONER_STYLE}
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className={cn(
            "bg-popover text-popover-foreground data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95 data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[ending-style]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex max-h-(--available-height) max-w-(--available-width) min-w-(--anchor-width) origin-(--transform-origin) flex-col overflow-hidden rounded-md border p-0 shadow-md outline-hidden",
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

type ComboboxSearchProps = Omit<ComboboxPrimitive.Input.Props, "className"> & {
  className?: string;
  /** Rendered after the input, e.g. a close button. */
  addon?: React.ReactNode;
};

/** The search input, inside the popup above the list. */
function ComboboxSearch({ className, addon, ...props }: ComboboxSearchProps) {
  return (
    <div
      data-slot="combobox-search"
      className="flex h-9 shrink-0 items-center gap-2 border-b px-3"
    >
      <MagnifyingGlassIcon className="size-4 shrink-0 opacity-50" />
      <ComboboxPrimitive.Input
        data-slot="combobox-input"
        className={cn(
          "placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
      {addon}
    </div>
  );
}

type ComboboxListProps = Omit<ComboboxPrimitive.List.Props, "className"> & {
  className?: string;
};

function ComboboxList({ className, ...props }: ComboboxListProps) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto overscroll-contain p-1",
        className,
      )}
      {...props}
    />
  );
}

type ComboboxItemProps = Omit<ComboboxPrimitive.Item.Props, "className"> & {
  className?: string;
};

function ComboboxItem({ className, ...props }: ComboboxItemProps) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

type ComboboxGroupProps = Omit<ComboboxPrimitive.Group.Props, "className"> & {
  className?: string;
};

function ComboboxGroup({ className, ...props }: ComboboxGroupProps) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn("text-foreground", className)}
      {...props}
    />
  );
}

type ComboboxGroupLabelProps = Omit<
  ComboboxPrimitive.GroupLabel.Props,
  "className"
> & {
  className?: string;
};

function ComboboxGroupLabel({ className, ...props }: ComboboxGroupLabelProps) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-group-label"
      className={cn(
        "text-muted-foreground px-2 py-1.5 text-xs font-medium",
        className,
      )}
      {...props}
    />
  );
}

type ComboboxSeparatorProps = Omit<
  ComboboxPrimitive.Separator.Props,
  "className"
> & {
  className?: string;
};

function ComboboxSeparator({ className, ...props }: ComboboxSeparatorProps) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn("bg-border -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

/** A centred status line in the list: empty, loading or error. */
function ComboboxMessage({
  className,
  tone = "muted",
  ...props
}: React.ComponentProps<"div"> & { tone?: "muted" | "destructive" }) {
  return (
    <div
      data-slot="combobox-message"
      className={cn(
        "py-6 text-center text-sm",
        tone === "destructive" && "text-destructive",
        className,
      )}
      {...props}
    />
  );
}

/** One selected value in the trigger. `onRemove` adds its ✕. */
function ComboboxChip({
  className,
  children,
  onRemove,
  removeLabel,
  ...props
}: React.ComponentProps<"span"> & {
  onRemove?: () => void;
  removeLabel?: string;
}) {
  return (
    <span
      data-slot="combobox-chip"
      className={cn(
        "bg-secondary text-secondary-foreground inline-flex max-w-full shrink-0 items-center gap-1 rounded px-2 py-0.5 text-xs whitespace-nowrap",
        className,
      )}
      {...props}
    >
      <span className="min-w-0 truncate">{children}</span>
      {onRemove && (
        // A span, not a button: the whole trigger is already a <button>. It
        // swallows the pointer so removing a value doesn't toggle the popup.
        <span
          role="button"
          tabIndex={0}
          aria-label={removeLabel}
          data-slot="combobox-chip-remove"
          className="shrink-0 cursor-pointer rounded-sm opacity-60 hover:opacity-100"
          onPointerDown={(event) => {
            event.stopPropagation();
            event.preventDefault();
          }}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onRemove();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.stopPropagation();
              event.preventDefault();
              onRemove();
            }
          }}
        >
          <XIcon className="h-3 w-3" />
        </span>
      )}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                The picker                                  */
/* -------------------------------------------------------------------------- */

export type ComboboxLabels = {
  placeholder: React.ReactNode;
  search: string;
  empty: React.ReactNode;
  loading: React.ReactNode;
  /** Shown when `error` is `true` rather than a message of its own. */
  error: React.ReactNode;
  clear: React.ReactNode;
  loadMore: React.ReactNode;
  close: string;
  selectAll: React.ReactNode;
  deselectAll: React.ReactNode;
  /** The ✕ on a chip: an accessible name, so a string. */
  remove: (label: string) => string;
  /** The overflow chip after `chips.max` values. */
  more: (count: number) => React.ReactNode;
  /** The creatable row. */
  create: (query: string) => React.ReactNode;
};

const DEFAULT_LABELS: ComboboxLabels = {
  placeholder: "Select option...",
  search: "Search...",
  empty: "No results.",
  loading: "Loading…",
  error: "Something went wrong.",
  clear: "Clear",
  loadMore: "Load more",
  close: "Close",
  selectAll: "Select All",
  deselectAll: "Deselect All",
  remove: (label) => `Remove ${label}`,
  more: (count) => `+${count} more`,
  create: (query) => `Add “${query}”`,
};

export type ComboboxItemGroup<T> = {
  /** Omit for a run of items with no heading. */
  label?: string;
  items: readonly T[];
};

export type ComboboxLoadMore = {
  hasMore: boolean;
  isLoading?: boolean;
  onLoadMore: () => void;
  /** Shown instead of the row when more exist but can't be loaded. */
  exhaustedMessage?: React.ReactNode;
};

export type ComboboxValueContext<T> = {
  /** The selection, resolved to items (values never seen are left out). */
  selected: T[];
  remove: (value: string) => void;
};

type ComboboxSource<T> =
  | { items: readonly T[]; groups?: never }
  | { groups: readonly ComboboxItemGroup<T>[]; items?: never };

type ComboboxSelection<T> =
  | {
      multiple?: false;
      value: string | null | undefined;
      onValueChange: (value: string | null, item: T | null) => void;
      /** Picking the selected item again clears it. */
      deselectable?: boolean;
    }
  | {
      multiple: true;
      value: readonly string[] | undefined;
      onValueChange: (values: string[], items: T[]) => void;
      /**
       * Chips in the trigger. `overflow` keeps one row and ends with a
       * "+N more" chip; `wrap` shows every chip with a ✕ and lets the trigger
       * grow. `sort` orders them (selection order by default).
       */
      chips?: {
        layout?: "overflow" | "wrap";
        max?: number;
        sort?: (a: T, b: T) => number;
      };
      /** A Select All / Deselect All row over the visible items. */
      selectAll?: boolean;
      /** Group headings toggle their whole group. */
      groupToggle?: boolean;
    };

export type ComboboxProps<T> = ComboboxSource<T> &
  ComboboxSelection<T> & {
    getValue: (item: T) => string;
    getLabel: (item: T) => string;

    /** Controlled query; omit to let the picker own it. */
    search?: string;
    onSearchChange?: (query: string) => void;
    /**
     * Client-side matching. Defaults to a case-insensitive substring of the
     * label or value; `null` shows `items` as given (the caller filtered).
     */
    filter?: ((item: T, query: string) => boolean) | null;
    /**
     * Server-side search: called `debounceMs` after the query settles, with a
     * signal aborted by the next call. Turns client filtering off and pins the
     * selection above the results.
     */
    onSearch?: (query: string, signal: AbortSignal) => void;
    debounceMs?: number;
    /** List the selection first, whatever the query. */
    pinSelected?: boolean;
    /** Empty the query when the popup closes. Defaults to true. */
    resetSearchOnClose?: boolean;
    /**
     * Resolve the selection against every item seen (the default), so a
     * search that replaces `items` keeps naming it. `false` resolves against
     * the current `items` only: a value they no longer hold shows as unset.
     */
    rememberItems?: boolean;
    /**
     * The selection's items, when the caller holds them — for values that are
     * not among `items`, such as created ones. Takes precedence over lookup.
     */
    selectedItems?: readonly T[];

    loading?: boolean;
    /** `true` shows `labels.error`; a node shows itself. */
    error?: React.ReactNode;
    /** Offers the query as a new item. */
    onCreate?: (query: string) => void;
    /** Whether the query can be created; by default, unless a label matches. */
    isCreatable?: (query: string) => boolean;
    loadMore?: ComboboxLoadMore;
    allowClear?: boolean;

    /** Extra classes for one item's row. */
    itemClassName?: (item: T) => string | undefined;
    /** The row's content in place of the label. */
    renderItem?: (item: T, state: { selected: boolean }) => React.ReactNode;
    /** The trigger's content when something is selected. */
    renderValue?: (context: ComboboxValueContext<T>) => React.ReactNode;
    /** What marks a selected row; `none` leaves it to `renderItem`. */
    indicator?: "check" | "checkbox" | "none";
    /**
     * Highlight the first row when the list opens and as the query changes,
     * so Enter picks the top hit. Defaults to true.
     */
    highlightFirst?: boolean;
    /** A ✕ beside the search input that closes the popup. */
    closeButton?: boolean;

    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Defaults to true inside a `DialogContent`. */
    modal?: boolean;
    disabled?: boolean;
    id?: string;
    "aria-label"?: string;
    "aria-invalid"?: boolean;
    className?: string;
    contentClassName?: string;
    contentStyle?: React.CSSProperties;
    container?: ComboboxContentProps["container"];
    labels?: Partial<ComboboxLabels>;
  };

const DEFAULT_MAX_CHIPS = 3;
const DEFAULT_DEBOUNCE_MS = 250;

/** Every item seen, by value — so a selection outlives the items naming it. */
function useItemCache<T>(
  sections: Section<T>[],
  getValue: (item: T) => string,
  remember: boolean,
): Map<string, T> {
  // A Map held in state, not a ref, so it stays stable across renders.
  const [remembered] = React.useState(() => new Map<string, T>());
  // Without remembering, only the items on screen are kept, so repeated
  // searches don't pile up results nothing will pick.
  const cache = remember ? remembered : new Map<string, T>();
  for (const section of sections) {
    for (const item of section.items) {
      cache.set(getValue(item), item);
    }
  }
  return cache;
}

/** State that is controlled when the prop is given, internal otherwise. */
function useControllable<V>(
  controlled: V | undefined,
  initial: V,
): [V, (next: V) => void] {
  const [internal, setInternal] = React.useState(initial);
  return [controlled ?? internal, setInternal];
}

/** Debounced server search; each call aborts the previous one. */
function useDebouncedSearch(
  query: string,
  onSearch: ComboboxProps<unknown>["onSearch"],
  debounceMs: number,
) {
  const onSearchRef = React.useRef(onSearch);
  React.useEffect(() => {
    onSearchRef.current = onSearch;
  });
  const controllerRef = React.useRef<AbortController | null>(null);
  const isFirstRun = React.useRef(true);
  const enabled = onSearch !== undefined;

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (!enabled) return;
    const timer = setTimeout(() => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      onSearchRef.current?.(query, controller.signal);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs, enabled]);

  React.useEffect(() => () => controllerRef.current?.abort(), []);
}

function SelectAllRow({
  state,
  labels,
  onToggle,
}: {
  state: CheckedState;
  labels: ComboboxLabels;
  onToggle: () => void;
}) {
  return (
    <div
      role="menuitemcheckbox"
      aria-checked={state === "indeterminate" ? "mixed" : state}
      tabIndex={0}
      data-slot="combobox-select-all"
      className="hover:bg-accent flex w-full shrink-0 cursor-pointer items-center gap-2 border-b px-3 py-2 text-left"
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <Checkbox
        checked={state}
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none"
      />
      <span className="text-sm font-medium">
        {state === true ? labels.deselectAll : labels.selectAll}
      </span>
    </div>
  );
}

type ChipsConfig<T> = Extract<
  ComboboxSelection<T>,
  { multiple: true }
>["chips"];

/** The selection as chips: every one with a ✕ (`wrap`), or a row then "+N". */
function ChipValues<T>({
  selected,
  chips,
  getLabel,
  getValue,
  labels,
  remove,
}: {
  selected: T[];
  chips: ChipsConfig<T>;
  getLabel: (item: T) => string;
  getValue: (item: T) => string;
  labels: ComboboxLabels;
  remove: (value: string) => void;
}) {
  const ordered = chips?.sort ? [...selected].sort(chips.sort) : selected;
  if (chips?.layout === "wrap") {
    return (
      <span className="flex min-w-0 flex-1 flex-wrap gap-1 py-0.5">
        {ordered.map((item) => (
          <ComboboxChip
            key={getValue(item)}
            onRemove={() => remove(getValue(item))}
            removeLabel={labels.remove(getLabel(item))}
          >
            {getLabel(item)}
          </ComboboxChip>
        ))}
      </span>
    );
  }
  const max = chips?.max ?? DEFAULT_MAX_CHIPS;
  return (
    <span className="flex gap-1 overflow-hidden">
      {ordered.slice(0, max).map((item) => (
        <ComboboxChip key={getValue(item)}>{getLabel(item)}</ComboboxChip>
      ))}
      {ordered.length > max && (
        <ComboboxChip>{labels.more(ordered.length - max)}</ComboboxChip>
      )}
    </span>
  );
}

function TriggerValue<T>({
  selected,
  multiple,
  chips,
  getLabel,
  getValue,
  labels,
  renderValue,
  remove,
}: {
  selected: T[];
  multiple: boolean;
  chips: ChipsConfig<T>;
  getLabel: (item: T) => string;
  getValue: (item: T) => string;
  labels: ComboboxLabels;
  renderValue: ComboboxProps<T>["renderValue"];
  remove: (value: string) => void;
}) {
  if (selected.length === 0) {
    return (
      <span className="text-muted-foreground truncate">
        {labels.placeholder}
      </span>
    );
  }
  if (renderValue) {
    return (
      <span className="truncate">{renderValue({ selected, remove })}</span>
    );
  }
  if (!multiple) {
    return <span className="truncate">{getLabel(selected[0])}</span>;
  }
  return (
    <ChipValues
      selected={selected}
      chips={chips}
      getLabel={getLabel}
      getValue={getValue}
      labels={labels}
      remove={remove}
    />
  );
}

function ItemRow<T>({
  item,
  value,
  label,
  selected,
  indicator,
  renderItem,
  className,
}: {
  className?: string;
  item: T;
  value: string;
  label: string;
  selected: boolean;
  indicator: NonNullable<ComboboxProps<T>["indicator"]>;
  renderItem: ComboboxProps<T>["renderItem"];
}) {
  const content = renderItem ? (
    renderItem(item, { selected })
  ) : (
    <span>{label}</span>
  );
  return (
    <ComboboxItem value={value} className={className}>
      {indicator === "check" && (
        <CheckIcon
          className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")}
        />
      )}
      {indicator === "checkbox" && (
        <Checkbox checked={selected} aria-hidden tabIndex={-1} />
      )}
      {indicator === "checkbox" ? (
        <span className="flex-1">{content}</span>
      ) : (
        content
      )}
    </ComboboxItem>
  );
}

/** The query and open state, each controlled or internal. */
function useQueryAndOpen<T>(props: ComboboxProps<T>) {
  const {
    search,
    onSearchChange,
    onSearch,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    resetSearchOnClose = true,
    open: controlledOpen,
    onOpenChange,
  } = props;
  const [open, setOpen] = useControllable(controlledOpen, false);
  const [query, setQuery] = useControllable(search, "");
  useDebouncedSearch(query, onSearch, debounceMs);

  const changeSearch = (next: string) => {
    setQuery(next);
    onSearchChange?.(next);
  };
  const changeOpen = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
    if (!next && resetSearchOnClose && query !== "") changeSearch("");
  };
  return { open, query, changeSearch, changeOpen };
}

/** The selection resolved to items, and how a new one is emitted. */
function useSelection<T>(props: ComboboxProps<T>, sections: Section<T>[]) {
  const { getValue, rememberItems = true, selectedItems } = props;
  const lookup = new Map(useItemCache(sections, getValue, rememberItems));
  for (const item of selectedItems ?? []) lookup.set(getValue(item), item);

  const values = selectionValues(props.value);
  const valueSet = new Set(values);
  const isSelected = (item: T) => valueSet.has(getValue(item));

  const emit = (nextValues: string[]) => {
    const items = resolveItems(nextValues, lookup);
    if (props.multiple) {
      props.onValueChange(nextValues, items);
    } else {
      props.onValueChange(nextValues[0] ?? null, items[0] ?? null);
    }
  };
  return {
    cache: lookup,
    values,
    selected: resolveItems(values, lookup),
    isSelected,
    emit,
  };
}

/** What the list shows for the current query. */
function listing<T>(
  props: ComboboxProps<T>,
  sections: Section<T>[],
  selected: T[],
  query: string,
) {
  const { getValue, getLabel, onSearch, onCreate } = props;
  const serverSearch = onSearch !== undefined;
  const listed = visibleSections({
    sections,
    query,
    filter: resolveFilter(props.filter, serverSearch, () =>
      defaultFilter(getLabel, getValue),
    ),
    pin: props.pinSelected ?? serverSearch,
    selected,
    getValue,
  });
  const listedItems = listed.flatMap((section) => section.items);
  const trimmedQuery = query.trim();
  const creatable =
    onCreate !== undefined &&
    canCreate({
      query: trimmedQuery,
      isCreatable: props.isCreatable,
      listedItems,
      getLabel,
    });
  return { listed, listedItems, trimmedQuery, creatable };
}

function StatusMessage({
  error,
  loading,
  listedCount,
  creatable,
  labels,
}: {
  error: React.ReactNode;
  loading: boolean;
  listedCount: number;
  creatable: boolean;
  labels: ComboboxLabels;
}) {
  const kind = statusKind({
    error: Boolean(error),
    loading,
    listedCount,
    creatable,
  });
  if (kind === "error") {
    return (
      <ComboboxMessage tone="destructive" role="alert">
        {error === true ? labels.error : error}
      </ComboboxMessage>
    );
  }
  if (kind === "loading") {
    return <ComboboxMessage role="status">{labels.loading}</ComboboxMessage>;
  }
  return kind === "empty" ? (
    <ComboboxMessage>{labels.empty}</ComboboxMessage>
  ) : null;
}

function LoadMoreRow({
  loadMore,
  labels,
}: {
  loadMore: ComboboxLoadMore | undefined;
  labels: ComboboxLabels;
}) {
  if (loadMore?.hasMore) {
    return (
      <ComboboxGroup className="p-1">
        <ComboboxItem
          value={LOAD_MORE}
          disabled={loadMore.isLoading}
          className="text-muted-foreground justify-center"
        >
          {loadMore.isLoading ? labels.loading : labels.loadMore}
        </ComboboxItem>
      </ComboboxGroup>
    );
  }
  if (!loadMore?.exhaustedMessage) return null;
  return (
    <div className="text-muted-foreground px-2 py-1.5 text-center text-xs">
      {loadMore.exhaustedMessage}
    </div>
  );
}

/** The rows after the items: create, status, load more and clear. */
function ListFooter<T>({
  props,
  listedCount,
  creatable,
  trimmedQuery,
  labels,
}: {
  props: ComboboxProps<T>;
  listedCount: number;
  creatable: boolean;
  trimmedQuery: string;
  labels: ComboboxLabels;
}) {
  const { loading = false, loadMore, allowClear = false } = props;
  return (
    <>
      {creatable && (
        <ComboboxGroup className="p-1">
          <ComboboxItem value={CREATE} className="text-primary">
            <PlusIcon className="h-4 w-4 text-current" />
            <span>{labels.create(trimmedQuery)}</span>
          </ComboboxItem>
        </ComboboxGroup>
      )}
      <StatusMessage
        error={props.error}
        loading={loading}
        listedCount={listedCount}
        creatable={creatable}
        labels={labels}
      />
      {loading && listedCount > 0 && !loadMore && (
        <ComboboxMessage role="status" className="py-2 text-xs">
          {labels.loading}
        </ComboboxMessage>
      )}
      <LoadMoreRow loadMore={loadMore} labels={labels} />
      {allowClear && (
        <ComboboxGroup className="bg-popover sticky bottom-0 p-1">
          <ComboboxSeparator />
          <ComboboxItem value={CLEAR} className="justify-center">
            {labels.clear}
          </ComboboxItem>
        </ComboboxGroup>
      )}
    </>
  );
}

function SectionLabel<T>({
  section,
  groupToggle,
  isSelected,
  onToggle,
}: {
  section: Section<T>;
  groupToggle: boolean;
  isSelected: (item: T) => boolean;
  onToggle: () => void;
}) {
  if (!section.label) return null;
  if (!groupToggle) {
    return <ComboboxGroupLabel>{section.label}</ComboboxGroupLabel>;
  }
  return (
    <ComboboxGroupLabel
      className="bg-muted/30 text-foreground flex cursor-pointer items-center gap-2 border-b px-3 py-2 text-sm"
      onClick={onToggle}
    >
      <Checkbox
        checked={checkState(section.items, isSelected)}
        tabIndex={-1}
        className="pointer-events-none"
      />
      <span>{section.label}</span>
    </ComboboxGroupLabel>
  );
}

function Sections<T>({
  props,
  listed,
  isSelected,
  toggleAll,
}: {
  props: ComboboxProps<T>;
  listed: Section<T>[];
  isSelected: (item: T) => boolean;
  toggleAll: (items: T[]) => void;
}) {
  const { getValue, getLabel, renderItem, itemClassName } = props;
  const indicator = props.indicator ?? (props.multiple ? "checkbox" : "check");
  const groupToggle = props.multiple === true && props.groupToggle === true;
  return listed.map((section) => (
    <ComboboxGroup key={section.key} className="p-1">
      <SectionLabel
        section={section}
        groupToggle={groupToggle}
        isSelected={isSelected}
        onToggle={() => toggleAll(section.items)}
      />
      {section.items.map((item) => (
        <ItemRow
          key={`${section.key}:${getValue(item)}`}
          item={item}
          value={getValue(item)}
          label={getLabel(item)}
          selected={isSelected(item)}
          indicator={indicator}
          renderItem={renderItem}
          className={itemClassName?.(item)}
        />
      ))}
    </ComboboxGroup>
  ));
}

function CloseButton({
  label,
  onClose,
}: {
  label: string;
  onClose: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClose}
      className="hover:bg-accent rounded-sm p-1"
    >
      <XIcon className="h-4 w-4" />
    </button>
  );
}

function Combobox<T>(props: ComboboxProps<T>) {
  const { getValue, getLabel, loadMore, highlightFirst = true } = props;
  const labels = { ...DEFAULT_LABELS, ...props.labels };
  const isInsideDialog = useIsInsideDialog();

  const { open, query, changeSearch, changeOpen } = useQueryAndOpen(props);
  const sections = toSections<T>(props);
  const { cache, values, selected, isSelected, emit } = useSelection(
    props,
    sections,
  );
  const { listed, listedItems, trimmedQuery, creatable } = listing(
    props,
    sections,
    selected,
    query,
  );

  // What the keyboard would pick; Enter with nothing highlighted creates.
  const highlightedRef = React.useRef<string | undefined>(undefined);

  const remove = (value: string) =>
    emit(values.filter((current) => current !== value));

  const runAction = (action: string) => {
    if (action === CREATE) {
      props.onCreate?.(trimmedQuery);
      changeSearch("");
    } else if (action === LOAD_MORE) {
      if (loadMore?.hasMore && !loadMore.isLoading) loadMore.onLoadMore();
    } else if (action === CLEAR) {
      emit([]);
      changeOpen(false);
    }
  };

  const handleValueChange = (
    next: string | string[] | null,
    details: ComboboxPrimitive.Root.ChangeEventDetails,
  ) => {
    const change = decideValueChange(
      next,
      values,
      !props.multiple && props.deselectable === true,
    );
    if (change.kind === "action") {
      details.cancel();
      runAction(change.action);
      return;
    }
    emit(change.values);
  };

  const toggleAll = (items: T[]) =>
    emit(toggleAllValues(values, items.map(getValue)));

  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!loadMore?.hasMore || loadMore.isLoading) return;
    if (nearListEnd(event.currentTarget)) loadMore.onLoadMore();
  };

  const wrapChips = props.multiple && props.chips?.layout === "wrap";

  return (
    <ComboboxRoot<string, boolean>
      multiple={props.multiple ?? false}
      value={props.multiple ? values : (values[0] ?? null)}
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={(next) => changeOpen(next)}
      inputValue={query}
      onInputValueChange={(next, details) => {
        // Base UI empties the input after a multiple-mode pick and on close;
        // the query is ours, so only typing changes it.
        if (details.reason === "input-change") changeSearch(next);
      }}
      onItemHighlighted={(value) => {
        highlightedRef.current = value;
      }}
      // Every navigable row's value, in render order. Without it Base UI
      // re-highlights the first row only on open or a query change, so server
      // results that land after the query leave nothing highlighted and Enter
      // picks nothing. (`items` would do the same but switches Base UI to
      // opening on an animation frame.)
      filteredItems={navigableValues({
        listedValues: listedItems.map(getValue),
        creatable,
        hasMore: loadMore?.hasMore === true,
        allowClear: props.allowClear === true,
      })}
      filter={null}
      autoHighlight={highlightFirst ? "always" : false}
      itemToStringLabel={(value) => {
        const item = cache.get(value);
        return item === undefined ? value : getLabel(item);
      }}
      modal={props.modal ?? isInsideDialog}
      disabled={props.disabled}
    >
      <ComboboxTrigger
        id={props.id}
        aria-label={props["aria-label"]}
        aria-invalid={props["aria-invalid"]}
        className={cn(
          wrapChips ? "h-auto min-h-8 whitespace-normal" : "h-8",
          props.className,
        )}
      >
        <TriggerValue
          selected={selected}
          multiple={props.multiple ?? false}
          chips={props.multiple ? props.chips : undefined}
          getLabel={getLabel}
          getValue={getValue}
          labels={labels}
          renderValue={props.renderValue}
          remove={remove}
        />
      </ComboboxTrigger>
      <ComboboxContent
        container={props.container}
        className={props.contentClassName}
        style={props.contentStyle}
      >
        <ComboboxSearch
          placeholder={labels.search}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              creatable &&
              highlightedRef.current === undefined
            ) {
              event.preventDefault();
              runAction(CREATE);
            }
          }}
          addon={
            props.closeButton ? (
              <CloseButton
                label={labels.close}
                onClose={() => changeOpen(false)}
              />
            ) : null
          }
        />
        {props.multiple && props.selectAll && listedItems.length > 0 && (
          <SelectAllRow
            state={checkState(listedItems, isSelected)}
            labels={labels}
            onToggle={() => toggleAll(listedItems)}
          />
        )}
        <ComboboxList
          aria-busy={props.loading === true || undefined}
          onScroll={handleListScroll}
        >
          <Sections
            props={props}
            listed={listed}
            isSelected={isSelected}
            toggleAll={toggleAll}
          />
          <ListFooter
            props={props}
            listedCount={listedItems.length}
            creatable={creatable}
            trimmedQuery={trimmedQuery}
            labels={labels}
          />
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
}

export {
  Combobox,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxSearch,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxSeparator,
  ComboboxMessage,
  ComboboxChip,
};
export type { ComboboxRootProps, ComboboxContentProps };
